interface VerifyEmailPayload {
  new_email: string;
  previous_email: string;
  web_user_uuid: string;
}

interface VerifyEmailContext {
  newEmail: string;
  previousEmail: string;
  payload: VerifyEmailPayload;
  response?: any;
}

interface UseVerifyEmailOptions {
  disableWhenError?: boolean;
  setInputErrorOnInvalid?: boolean;
  setInputErrorOnError?: boolean;
  onInvalidEmail?: (context: VerifyEmailContext) => void | Promise<void>;
  onSuccess?: (context: VerifyEmailContext) => void | Promise<void>;
  onError?: (context: VerifyEmailContext) => void | Promise<void>;
  onBeforeRequest?: (context: VerifyEmailContext) => void | Promise<void>;
  onAfterRequest?: (context: VerifyEmailContext) => void | Promise<void>;
}

export function useVerifyEmail(options: UseVerifyEmailOptions = {}) {
  const store = useKegelAnalyticStore();
  const API = useKegelApi();
  const { $logger } = useNuxtApp();
  const { checkEmailFormat, useCheckDomainExists } = useEmail();

  const newEmail = ref('');
  const showInputError = ref(false);
  const isLoading = ref(false);

  const setInputErrorOnInvalid = options.setInputErrorOnInvalid ?? true;
  const setInputErrorOnError = options.setInputErrorOnError ?? true;

  const isDisabledButton = computed(() => {
    const hasFormatError = !checkEmailFormat(newEmail.value);
    if (options.disableWhenError) {
      return hasFormatError || showInputError.value;
    }

    return hasFormatError;
  });

  function buildContext(response?: any): VerifyEmailContext {
    return {
      newEmail: newEmail.value,
      previousEmail: store.userEmail,
      payload: {
        new_email: newEmail.value,
        previous_email: store.userEmail,
        web_user_uuid: store.userUuid,
      },
      response,
    };
  }

  async function validateEmail(): Promise<boolean> {
    isLoading.value = true;
    const isValid = await useCheckDomainExists(newEmail.value);

    if (!isValid) {
      const context = buildContext();
      if (options.onInvalidEmail) {
        await options.onInvalidEmail(context);
      }
      if (setInputErrorOnInvalid) {
        showInputError.value = true;
      }
      isLoading.value = false;
      return false;
    }

    return changeEmail();
  }

  async function changeEmail(): Promise<boolean> {
    isLoading.value = true;
    const context = buildContext();

    if (options.onBeforeRequest) {
      await options.onBeforeRequest(context);
    }

    const response = await API.changeUserEmail(context.payload);
    context.response = response;

    if (response.ok) {
      store.userEmail = newEmail.value;
      if (options.onSuccess) {
        await options.onSuccess(context);
      }
    } else {
      $logger.error(response.error, {
        tags: {
          flow: 'account',
          step: 'change_email',
        },
        extra: {
          userUuid: store.userUuid,
        },
      });

      if (options.onError) {
        await options.onError(context);
      }
      if (setInputErrorOnError) {
        showInputError.value = true;
      }
    }

    if (options.onAfterRequest) {
      await options.onAfterRequest(context);
    }

    isLoading.value = false;
    return response.ok;
  }

  watch(newEmail, () => {
    showInputError.value = false;
  });

  return {
    newEmail,
    showInputError,
    isLoading,
    isDisabledButton,
    validateEmail,
  };
}
